import React, { useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../../context/AuthContext";
import { comprimirImagen } from "../../../utils/comprimirImagen";
import { actualizarNota, crearNota, formatearPeso, mensajeError, subirArchivosNota } from "../services/notasService";

const hoy = () => {
  const d = new Date();
  const dos = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`;
};

// Crear / modificar una nota. Al crear se pueden elegir archivos: se suben justo después de guardar la nota.
// Las imágenes se achican antes de subirlas (una foto de celular pasa de varios MB a unos cientos de KB).
export default function NotaFormDialog({ open, nota, catalogos, onClose, onGuardado }) {
  const { user } = useAuth();
  const sucursalUsuario = user?.sucursal;
  const nombreUsuario = String(user?.username || "").slice(0, 20);
  const esEdicion = Boolean(nota);
  const [form, setForm] = useState({});
  const [archivos, setArchivos] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const sucursalInicial = catalogos.sucursales.includes(sucursalUsuario) ? sucursalUsuario : catalogos.sucursales[0] || "";
    setForm(
      nota
        ? { fecha: nota.fecha, sucursal: nota.sucursal, area: nota.area, destinatario: nota.destinatario, referencia: nota.referencia || "", enviado_por: nota.enviado_por || "", observacion: nota.observacion || "" }
        : { fecha: hoy(), sucursal: sucursalInicial, area: catalogos.areas[0] || "", destinatario: "", referencia: "", enviado_por: nombreUsuario, observacion: "" }
    );
    setArchivos([]);
    setError("");
  }, [open, nota, catalogos, sucursalUsuario, nombreUsuario]);

  const cambiar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const elegirArchivos = async (e) => {
    const lista = Array.from(e.target.files || []);
    e.target.value = "";
    const listos = await Promise.all(lista.map(async (f) => (f.type.startsWith("image/") ? (await comprimirImagen(f)).file : f)));
    setArchivos((prev) => [...prev, ...listos].slice(0, 10));
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    setError("");
    try {
      const { data } = esEdicion ? await actualizarNota(nota.id, form) : await crearNota(form);
      const id = esEdicion ? nota.id : data.id;
      let aviso = "";
      if (archivos.length) {
        try {
          await subirArchivosNota(id, archivos);
        } catch (err) {
          aviso = `La nota ${id} se guardó, pero no se pudieron subir los archivos: ${mensajeError(err, "error desconocido")}. Súbelos desde "Archivos".`;
        }
      }
      onGuardado({ id, nueva: !esEdicion, aviso });
    } catch (err) {
      setError(mensajeError(err, "No se pudo guardar la nota."));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={guardando ? undefined : onClose} maxWidth="sm" fullWidth component="form" onSubmit={guardar}>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 800 }}>{esEdicion ? `Modificar nota ${nota.id}` : "Agregar nota"}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField size="small" type="date" label="Fecha" required fullWidth value={form.fecha || ""} onChange={cambiar("fecha")} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" select label="Sucursal" required fullWidth value={form.sucursal || ""} onChange={cambiar("sucursal")}>
              {catalogos.sucursales.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Stack>
          <TextField size="small" select label="Área" required fullWidth value={form.area || ""} onChange={cambiar("area")}>
            {catalogos.areas.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Destinatario" required fullWidth value={form.destinatario || ""} onChange={cambiar("destinatario")} slotProps={{ htmlInput: { maxLength: 150 } }} />
          <TextField size="small" label="Referencia" fullWidth value={form.referencia || ""} onChange={cambiar("referencia")} slotProps={{ htmlInput: { maxLength: 150 } }} />
          <TextField size="small" label="Enviado por" fullWidth value={form.enviado_por || ""} onChange={cambiar("enviado_por")} slotProps={{ htmlInput: { maxLength: 20 } }} />
          <TextField size="small" label="Observación" fullWidth multiline minRows={2} value={form.observacion || ""} onChange={cambiar("observacion")} slotProps={{ htmlInput: { maxLength: 255 } }} />

          <Box>
            <Button size="small" variant="outlined" startIcon={<AttachFileIcon />} onClick={() => inputRef.current?.click()} disabled={guardando}>
              {esEdicion ? "Agregar archivos" : "Adjuntar archivos"}
            </Button>
            <input ref={inputRef} type="file" multiple style={{ display: "none" }} onChange={elegirArchivos} />
            {archivos.map((f, i) => (
              <Stack key={`${f.name}-${i}`} direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                <Typography noWrap sx={{ flex: 1, fontSize: 13 }}>{f.name}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{formatearPeso(f.size)}</Typography>
                <IconButton size="small" aria-label="Quitar archivo" onClick={() => setArchivos((prev) => prev.filter((_, j) => j !== i))}><CloseIcon fontSize="small" /></IconButton>
              </Stack>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button type="submit" variant="contained" disabled={guardando}>
          {guardando ? <CircularProgress size={20} color="inherit" /> : esEdicion ? "Guardar cambios" : "Guardar nota"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
